import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TablePagination,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Drawer,
  Button,
  Grid,
  Autocomplete,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Popover,
  CircularProgress,
  Pagination,
  InputAdornment,
} from "@mui/material";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import TransgenderIcon from "@mui/icons-material/Transgender";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { BASE_URL, TARGET_TYPE } from "../../constants";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { authState } from "../../recoil/authAtom";
import Log from "../logDialog/Log";
import GetAppIcon from "@mui/icons-material/GetApp";
import excelIcon from "../../images/excel-icon.png";
import pdfIcon from "../../images/pdf-icon.png";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";

const editFieldStyle = {
  "& .MuiInput-root": {
    fontSize: "0.82rem",
    "&:before": { borderColor: "#e5e9ef" },
    "&:after": { borderColor: "#b93acf" },
  },
};

const drawerFieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    background: "#ffffff",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "#e5e9ef" },
    "&:hover fieldset": { borderColor: "#2a54a6" },
    "&.Mui-focused fieldset": { borderColor: "#b93acf", borderWidth: "1.5px" },
  },
};

const drawerLabelStyle = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "#0a1f44",
  letterSpacing: "0.2px",
  mb: 0.6,
};

const Req = () => (
  <Box component="span" sx={{ color: "#b93acf", ml: "2px", fontWeight: 700 }}>
    *
  </Box>
);

const PatientList = ({ doctors, setDoctors, centers, setCenters }) => {
  const user = useRecoilValue(authState);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [editPermission, setEditPermission] = useState(false);
  const [deletePermission, setDeletePermission] = useState(false);

  const getGenderIcon = (gender) => {
    const g = gender.toLowerCase();
    switch (g) {
      case "male":
        return <MaleIcon sx={{ color: "#2563eb" }} />;
      case "female":
        return <FemaleIcon sx={{ color: "#db2777" }} />;
      case "others":
        return <TransgenderIcon sx={{ color: "#7c3aed" }} />;
      default:
        return "";
    }
  };

  const [patients, setPatients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [patientSearchInput, setPatientSearchInput] = useState("");

  // ✅ Local cache for pagination and search
  const [cache, setCache] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  const fetchAllPatient = async (page = 0, limit = 10, query = "") => {
    setLoadingPatients(true);

    try {
      // ✅ Use search API if query exists
      const endpoint = query
        ? `${BASE_URL}api/user/common/patients/search-infos?query=${encodeURIComponent(
            query,
          )}&page=${page}&limit=${limit}`
        : `${BASE_URL}api/user/common/get-all-patient?page=${page}&limit=${limit}`;

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result.success) {
        // ✅ Cache key (includes query)
        const cacheKey = `${query}_${page}_${limit}`;

        // ✅ Update cache
        setCache((prev) => ({
          ...prev,
          [cacheKey]: { data: result.data, totalCount: result.totalCount },
        }));

        setPatients(result.data);
        setTotalCount(result.totalCount || result.data.length);
      } else {
        console.error("API failed:", result.message);
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong! Please try again.");
    } finally {
      setLoadingPatients(false);
    }
  };

  // Fetch or load from cache whenever pagination, search, or an explicit
  // refresh changes. Search requests are debounced; the initial empty search
  // loads immediately.
  useEffect(() => {
    const cacheKey = `${patientSearchInput}_${page}_${rowsPerPage}`;
    if (cache[cacheKey]) {
      setPatients(cache[cacheKey].data);
      setTotalCount(cache[cacheKey].totalCount);
      return undefined;
    }

    if (!patientSearchInput.trim()) {
      fetchAllPatient(page, rowsPerPage, patientSearchInput);
      return undefined;
    }

    const debounceTimer = setTimeout(() => {
      fetchAllPatient(page, rowsPerPage, patientSearchInput);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [page, rowsPerPage, patientSearchInput, reloadKey]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    setCache({}); // clear cache since limit changed
  };

  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const handleDownload = async () => {
    try {
      setExcelLoading(true);
      const response = await fetch(
        `${BASE_URL}api/user/common/export-excel-patient`,
        {
          method: "GET",
          headers: {},
        },
      );

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "patients.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setExcelLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setPdfLoading(true);
      const response = await fetch(
        `${BASE_URL}api/user/common/export-pdf-patient`,
        {
          method: "GET",
          headers: {},
        },
      );

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "patients.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleReload = () => {
    setCache({});
    setPage(0);
    setPatientSearchInput("");
    setReloadKey((previous) => previous + 1);
  };

  // Search Filter
  const handleSearch = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
    setPage(0); // Reset to first page on new search
  };

  const filteredPatients = patients.filter((patient) => {
    return Object.values(patient).some((value) =>
      String(value).toLowerCase().includes(searchQuery),
    );
  });

  // Calculate the rows to display for the current page
  const paginatedPatients = filteredPatients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleDelete = async (id) => {
    console.log(id);
    try {
      const response = await fetch(
        `${BASE_URL}api/user/common/patient-delete/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const data = await response.json();
      console.log(data);
      if (data.success) {
        alert(data.message); // Show success message to the user
        setPatients(patients.filter((patient) => patient._id !== id));
      } else {
        alert(`Error: ${data.message}`); // Show error message to the user
      }
    } catch (error) {
      alert("An unexpected error occurred while deleting the patient.");
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedPatient(null);
  };

  const showAge = (dob) => {
    const birthDate = dayjs(dob, "MMMM DD, YYYY");
    const today = dayjs();

    const years = today.diff(birthDate, "year");
    const months = today.diff(birthDate.add(years, "year"), "month");
    const days = today.diff(
      birthDate.add(years, "year").add(months, "month"),
      "day",
    );
    return `${years}Y ${months}M ${days}D`;
  };

  const calculateAgeToolTip = (dob) => {
    //if (!dob) return "Enter a valid DOB";
    const birthDate = dayjs(dob, "DD-MM-YY");
    //if (!birthDate.isValid()) return "Invalid DOB format";
    const now = dayjs();
    const years = now.diff(birthDate, "year");
    const months = now.diff(birthDate.add(years, "year"), "month");
    const days = now.diff(
      birthDate.add(years, "year").add(months, "month"),
      "day",
    );
    return `${years}Y ${months}M ${days}D`;
  };

  const handleDateChange1 = (newValue) => {
    const date = dayjs(newValue).format("DD-MM-YYYY");
    if (selectedPatient.age === "") {
      return setSelectedPatient({ ...selectedPatient, dob: date });
    } else {
      return setSelectedPatient({
        ...selectedPatient,
        dob: date,
        age: "",
      });
    }
  };

  const handleDateChange = (newValue) => {
    if (!newValue) return; // Ensure newValue is valid

    const formattedDate = dayjs(newValue).format("MMMM DD, YYYY"); // Store in original format
    setEditedPatient((prev) => ({
      ...prev,
      dob: formattedDate, // Keep stored format consistent
      age: prev.age ? "" : prev.age,
    }));
  };

  const handleAgeChange = (e) => {
    if (editedPatient.dob === "") {
      return setEditedPatient({
        ...editedPatient,
        age: e.target.value,
      });
    } else {
      return setEditedPatient({
        ...editedPatient,
        age: e.target.value,
        // dob: "",
      });
    }
  };

  const handleSubmitEdit = async () => {
    try {
      console.log("Editing", editedPatient);
      // return;
      let editData = { ...editedPatient };

      // Convert age to dob if age is provided
      if (parseInt(editData.age) > 0) {
        const currentYear = new Date().getFullYear();
        const yearOfBirth = currentYear - parseInt(editData.age);
        const dob = new Date(yearOfBirth, 0, 1); // January 1st of that year
        const formattedDob = `${dob.toLocaleString("en-US", {
          month: "long",
        })} ${dob.getDate().toString().padStart(2, "0")}, ${dob.getFullYear()}`;

        editData.dob = formattedDob;
      }

      delete editData.age; // Remove age field before sending
      console.log(editData);
      const response = await fetch(
        `${BASE_URL}api/user/common/patient-update/${editedPatient._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData),
        },
      );

      const data = await response.json();
      console.log("Update response:", data);

      if (data.success) {
        alert(data.message);

        const updatedList = patients.map((p) =>
          p._id === editedPatient._id ? { ...p, ...editData } : p,
        );
        setPatients(updatedList);

        // Reset state
        setEditRowIndex(null);
        setEditedPatient({});
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      alert("An unexpected error occurred while updating the patient.");
    }
  };

  const [openLogDialog, setOpenLogDialog] = useState(false);
  const [patientLogs, setPatientLogs] = useState(null);
  const handleViewLog = async (patientId) => {
    setOpenLogDialog(true);
    try {
      const response = await fetch(
        `${BASE_URL}api/user/common/get-logs?id=${patientId}&targetType=${TARGET_TYPE.patient}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const patients = await response.json();
      setPatientLogs(patients.logs);
    } catch (error) {
      console.error("Error:", error);
      alert("Something went wrong! Please try again.");
    } finally {
      // setLoading(false);
    }
  };
  const handleCloseLog = () => {
    setOpenLogDialog(false);
  };

  const [editRowIndex, setEditRowIndex] = useState(null);
  const [editedPatient, setEditedPatient] = useState({});

  const [loadingPatients, setLoadingPatients] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Box sx={{ mt: 1 }}>
        {/* Pagination inside card */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            borderBottom: "1px solid #e5e9ef",
            px: 1,
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                {
                  fontSize: "0.78rem",
                  color: "#64748b",
                },
              "& .MuiTablePagination-select": { fontSize: "0.78rem" },
              "& .MuiIconButton-root": { color: "#2a54a6" },
            }}
          />
        </Box>

        {/* ── Table ── */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "14px",
            border: "1px solid #e5e9ef",
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(10,31,68,0.06)",
          }}
        >
          {/* ── Toolbar: count + actions + search ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
              p: 1,
              flexWrap: "wrap",
            }}
          >
            {/* Patient count badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                background:
                  "linear-gradient(135deg, rgba(42,84,166,0.08), rgba(185,58,207,0.06))",
                border: "1px solid rgba(185,58,207,0.18)",
                borderRadius: "100px",
                px: 1.5,
                py: 0.6,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #2a54a6, #b93acf)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PersonIcon sx={{ color: "#fff", fontSize: "0.7rem" }} />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #2a54a6, #b93acf)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {totalCount} Patients
              </Typography>
            </Box>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Search */}
            <TextField
              placeholder="Search patients…"
              variant="outlined"
              size="small"
              value={patientSearchInput}
              onChange={(e) => setPatientSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: "1rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: "220px" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  background: "#ffffff",
                  fontSize: "0.82rem",
                  "& fieldset": { borderColor: "#e5e9ef" },
                  "&:hover fieldset": { borderColor: "#2a54a6" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#b93acf",
                    borderWidth: "1.5px",
                  },
                },
              }}
            />

            {/* Download */}
            <Tooltip title="Download data" arrow placement="top">
              <IconButton
                onClick={handleClick}
                size="small"
                sx={{
                  border: "1px solid #e5e9ef",
                  borderRadius: "10px",
                  p: "7px",
                  background: "#ffffff",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, rgba(42,84,166,0.07), rgba(185,58,207,0.05))",
                    borderColor: "rgba(185,58,207,0.25)",
                  },
                }}
              >
                <GetAppIcon sx={{ color: "#2a54a6", fontSize: "1.1rem" }} />
              </IconButton>
            </Tooltip>

            {/* Download popover */}
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: "12px",
                  border: "1px solid #e5e9ef",
                  boxShadow: "0 8px 24px rgba(10,31,68,0.12)",
                  p: 1.5,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  color: "#94a3b8",
                  mb: 1,
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                Export as
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {excelLoading ? (
                  <CircularProgress size={28} sx={{ color: "#b93acf" }} />
                ) : (
                  <Tooltip title="Excel" placement="bottom">
                    <Box
                      onClick={handleDownload}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        border: "1px solid #e5e9ef",
                        "&:hover": {
                          background: "rgba(42,84,166,0.06)",
                          borderColor: "#2a54a6",
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      <img src={excelIcon} style={{ height: 26, width: 26 }} />
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: "#64748b",
                          fontWeight: 500,
                        }}
                      >
                        Excel
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                {pdfLoading ? (
                  <CircularProgress size={28} sx={{ color: "#b93acf" }} />
                ) : (
                  <Tooltip title="PDF" placement="bottom">
                    <Box
                      onClick={handleDownloadPdf}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        border: "1px solid #e5e9ef",
                        "&:hover": {
                          background: "rgba(185,58,207,0.06)",
                          borderColor: "#b93acf",
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      <img src={pdfIcon} style={{ height: 26, width: 26 }} />
                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: "#64748b",
                          fontWeight: 500,
                        }}
                      >
                        PDF
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Popover>

            {/* Reload */}
            <Tooltip title="Refresh" arrow placement="top">
              <IconButton
                onClick={handleReload}
                size="small"
                sx={{
                  border: "1px solid #e5e9ef",
                  borderRadius: "10px",
                  p: "7px",
                  background: "#ffffff",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, rgba(42,84,166,0.07), rgba(185,58,207,0.05))",
                    borderColor: "rgba(185,58,207,0.25)",
                  },
                }}
              >
                <RefreshIcon sx={{ color: "#2a54a6", fontSize: "1.1rem" }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Table sx={{ backgroundColor: "#ffffff" }}>
            {/* Head */}
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(42,84,166,0.04), rgba(185,58,207,0.03))",
                  "& .MuiTableCell-head": {
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    py: "10px",
                    px: "14px",
                    borderBottom: "1px solid #e5e9ef",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Aadhar</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            {/* Loading bar */}
            {loadingPatients && (
              <TableRow>
                <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                  <LinearProgress
                    sx={{
                      height: 2,
                      background: "rgba(185,58,207,0.1)",
                      "& .MuiLinearProgress-bar": {
                        background: "linear-gradient(90deg, #2a54a6, #b93acf)",
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            )}

            <TableBody>
              {patients?.map((patient, index) => (
                <TableRow
                  key={index}
                  sx={{
                    transition: "background 0.2s ease",
                    "&:hover": {
                      background:
                        editRowIndex === index
                          ? "rgba(42,84,166,0.04)"
                          : "rgba(42,84,166,0.025)",
                    },
                    background:
                      editRowIndex === index
                        ? "rgba(42,84,166,0.04)"
                        : "transparent",
                    "& .MuiTableCell-root": {
                      fontSize: "0.82rem",
                      color: "#374151",
                      py: "10px",
                      px: "14px",
                      borderBottom: "1px solid #f1f5f9",
                    },
                    "&:last-child .MuiTableCell-root": { borderBottom: 0 },
                    "&:hover .viewLogBtn": {
                      opacity: 1,
                      visibility: "visible",
                    },
                    "&:hover .patientID": { transform: "translateY(-1px)" },
                  }}
                >
                  {editRowIndex === index ? (
                    /* ── EDIT MODE ── */
                    <>
                      <TableCell sx={{ position: "relative" }}>
                        <Box
                          className="patientID"
                          sx={{ transition: "transform 0.2s" }}
                        >
                          <Box
                            component="span"
                            sx={{
                              background:
                                "linear-gradient(135deg, #2a54a6, #b93acf)",
                              color: "#ffffff",
                              px: "8px",
                              py: "3px",
                              borderRadius: "6px",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              letterSpacing: "0.3px",
                              display: "inline-block",
                            }}
                          >
                            {patient.patientID}
                          </Box>
                        </Box>
                        {user?._doc?.role === "Admin" && (
                          <Typography
                            className="viewLogBtn"
                            onClick={() => handleViewLog(patient._id)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              position: "absolute",
                              top: "68%",
                              left: "14px",
                              opacity: 0,
                              visibility: "hidden",
                              transition: "all 0.2s ease",
                              background:
                                "linear-gradient(135deg, #2a54a6, #b93acf)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            View logs
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={editedPatient.name}
                          onChange={(e) =>
                            setEditedPatient({
                              ...editedPatient,
                              name: e.target.value,
                            })
                          }
                          variant="standard"
                          size="small"
                          sx={editFieldStyle}
                        />
                      </TableCell>

                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Tooltip
                            title={
                              editedPatient.dob
                                ? (() => {
                                    const d = new Date(editedPatient.dob);
                                    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
                                  })()
                                : ""
                            }
                            placement="top"
                            arrow
                            open
                          >
                            <input
                              type="date"
                              value={
                                editedPatient.dob
                                  ? new Date(
                                      editedPatient.dob,
                                    ).toLocaleDateString("en-CA")
                                  : ""
                              }
                              onChange={(e) => {
                                const [year, month, day] =
                                  e.target.value.split("-");
                                setEditedPatient((prev) => ({
                                  ...prev,
                                  dob: `${new Date(year, month - 1, day).toLocaleString("en-US", { month: "long" })} ${day.padStart(2, "0")}, ${year}`,
                                  age: "",
                                }));
                              }}
                              style={{
                                padding: "5px 6px",
                                border: "1px solid #e5e9ef",
                                borderRadius: "8px",
                                fontSize: "0.75rem",
                                color: "#374151",
                                background: "#f8fafc",
                                width: "36px",
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              color: "#94a3b8",
                            }}
                          >
                            OR
                          </Typography>
                          <input
                            type="number"
                            placeholder="Age"
                            value={editedPatient.age || ""}
                            onChange={handleAgeChange}
                            style={{
                              padding: "5px 6px",
                              border: "1px solid #e5e9ef",
                              borderRadius: "8px",
                              fontSize: "0.78rem",
                              color: "#374151",
                              background: "#f8fafc",
                              width: "52px",
                            }}
                          />
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Select
                          value={editedPatient.gender}
                          onChange={(e) =>
                            setEditedPatient({
                              ...editedPatient,
                              gender: e.target.value,
                            })
                          }
                          variant="standard"
                          size="small"
                          sx={{ fontSize: "0.82rem", color: "#374151" }}
                        >
                          <MenuItem value="Male" sx={{ fontSize: "0.82rem" }}>
                            Male
                          </MenuItem>
                          <MenuItem value="Female" sx={{ fontSize: "0.82rem" }}>
                            Female
                          </MenuItem>
                          <MenuItem value="Others" sx={{ fontSize: "0.82rem" }}>
                            Others
                          </MenuItem>
                        </Select>
                      </TableCell>

                      <TableCell>
                        <TextField
                          value={editedPatient.mobile}
                          onChange={(e) =>
                            setEditedPatient({
                              ...editedPatient,
                              mobile: e.target.value,
                            })
                          }
                          variant="standard"
                          size="small"
                          sx={editFieldStyle}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={editedPatient.address}
                          onChange={(e) =>
                            setEditedPatient({
                              ...editedPatient,
                              address: e.target.value,
                            })
                          }
                          variant="standard"
                          size="small"
                          sx={editFieldStyle}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={editedPatient.aadharNo}
                          onChange={(e) =>
                            setEditedPatient({
                              ...editedPatient,
                              aadharNo: e.target.value,
                            })
                          }
                          variant="standard"
                          size="small"
                          sx={editFieldStyle}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.75,
                          }}
                        >
                          <Tooltip title="Save" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleSubmitEdit();
                                setEditRowIndex(null);
                              }}
                              sx={{
                                background: "rgba(16,185,129,0.1)",
                                border: "1px solid rgba(16,185,129,0.25)",
                                borderRadius: "8px",
                                p: "5px",
                                "&:hover": {
                                  background: "rgba(16,185,129,0.2)",
                                },
                              }}
                            >
                              <CheckIcon
                                sx={{ fontSize: "0.95rem", color: "#059669" }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditRowIndex(null);
                                setEditedPatient({});
                              }}
                              sx={{
                                background: "rgba(239,68,68,0.08)",
                                border: "1px solid rgba(239,68,68,0.2)",
                                borderRadius: "8px",
                                p: "5px",
                                "&:hover": {
                                  background: "rgba(239,68,68,0.15)",
                                },
                              }}
                            >
                              <CloseIcon
                                sx={{ fontSize: "0.95rem", color: "#dc2626" }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </>
                  ) : (
                    /* ── VIEW MODE ── */
                    <>
                      <TableCell sx={{ position: "relative" }}>
                        <Box
                          className="patientID"
                          sx={{ transition: "transform 0.2s ease" }}
                        >
                          <Box
                            component="span"
                            sx={{
                              background:
                                "linear-gradient(135deg, #2a54a6, #b93acf)",
                              color: "#ffffff",
                              px: "8px",
                              py: "3px",
                              borderRadius: "6px",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              letterSpacing: "0.3px",
                              display: "inline-block",
                            }}
                          >
                            {patient.patientID}
                          </Box>
                        </Box>
                        {user?._doc?.role === "Admin" && (
                          <Typography
                            className="viewLogBtn"
                            onClick={() => handleViewLog(patient._id)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "0.68rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              position: "absolute",
                              top: "68%",
                              left: "14px",
                              opacity: 0,
                              visibility: "hidden",
                              transition: "all 0.2s ease",
                              background:
                                "linear-gradient(135deg, #2a54a6, #b93acf)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                            }}
                          >
                            View logs
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell
                        sx={{ fontWeight: 500, color: "#0a1f44 !important" }}
                      >
                        {patient.name}
                      </TableCell>
                      <TableCell>{showAge(patient.dob)}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              px: "8px",
                              py: "2px",
                              borderRadius: "100px",
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              background:
                                patient.gender === "Male"
                                  ? "rgba(42,84,166,0.08)"
                                  : patient.gender === "Female"
                                    ? "rgba(185,58,207,0.08)"
                                    : "rgba(148,163,184,0.15)",
                              color:
                                patient.gender === "Male"
                                  ? "#2a54a6"
                                  : patient.gender === "Female"
                                    ? "#b93acf"
                                    : "#64748b",
                              border:
                                patient.gender === "Male"
                                  ? "1px solid rgba(42,84,166,0.18)"
                                  : patient.gender === "Female"
                                    ? "1px solid rgba(185,58,207,0.18)"
                                    : "1px solid rgba(148,163,184,0.25)",
                            }}
                          >
                            {getGenderIcon(patient.gender)}
                            {patient.gender}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "#64748b !important" }}>
                        {patient.mobile || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: "#64748b !important",
                          maxWidth: 140,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {patient.address || "—"}
                      </TableCell>
                      <TableCell sx={{ color: "#64748b !important" }}>
                        {patient.aadharNo || "—"}
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.75,
                          }}
                        >
                          <Tooltip title="Edit patient" placement="left" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditRowIndex(index);
                                setEditedPatient({ ...patient });
                              }}
                              sx={{
                                background: "rgba(42,84,166,0.07)",
                                border: "1px solid rgba(42,84,166,0.15)",
                                borderRadius: "8px",
                                p: "5px",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, rgba(42,84,166,0.15), rgba(185,58,207,0.1))",
                                  borderColor: "rgba(185,58,207,0.25)",
                                },
                                transition: "all 0.2s",
                              }}
                            >
                              <EditIcon
                                sx={{ fontSize: "0.9rem", color: "#2a54a6" }}
                              />
                            </IconButton>
                          </Tooltip>
                          {!patient.inUse &&
                            user?._doc?.permissions?.patientManagement
                              ?.delete && (
                              <Tooltip
                                title="Delete patient"
                                placement="left"
                                arrow
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(patient._id)}
                                  sx={{
                                    background: "rgba(239,68,68,0.06)",
                                    border: "1px solid rgba(239,68,68,0.15)",
                                    borderRadius: "8px",
                                    p: "5px",
                                    "&:hover": {
                                      background: "rgba(239,68,68,0.14)",
                                      borderColor: "rgba(239,68,68,0.3)",
                                    },
                                    transition: "all 0.2s",
                                  }}
                                >
                                  <DeleteIcon
                                    sx={{
                                      fontSize: "0.9rem",
                                      color: "#dc2626",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                        </Box>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Edit Drawer ── */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "90vw", sm: "520px" },
            background: "#f8fafc",
            borderTopLeftRadius: "20px",
            borderBottomLeftRadius: "20px",
            boxShadow: "-8px 0 32px rgba(10,31,68,0.12)",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Drawer header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              background: "#ffffff",
              borderBottom: "1px solid #e5e9ef",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: "linear-gradient(90deg, #2a54a6, #b93acf)",
              },
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "9px",
                background: "linear-gradient(135deg, #2a54a6, #b93acf)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ color: "#fff", fontSize: "1rem" }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  color: "#0a1f44",
                  lineHeight: 1.2,
                }}
              >
                Edit Patient
              </Typography>
              <Typography
                sx={{ fontSize: "0.68rem", color: "#94a3b8", mt: "1px" }}
              >
                Update patient details below
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseDrawer}
              sx={{
                borderRadius: "8px",
                border: "1px solid #e5e9ef",
                p: "5px",
                "&:hover": {
                  background: "rgba(239,68,68,0.07)",
                  borderColor: "rgba(239,68,68,0.2)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: "1rem", color: "#94a3b8" }} />
            </IconButton>
          </Box>

          {/* Drawer body */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            {selectedPatient && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography sx={drawerLabelStyle}>Patient ID</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={selectedPatient.patientID}
                    InputProps={{ readOnly: true }}
                    sx={{
                      ...drawerFieldStyle,
                      "& .MuiOutlinedInput-root": {
                        ...drawerFieldStyle["& .MuiOutlinedInput-root"],
                        background: "#f1f5f9",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <Typography sx={drawerLabelStyle}>
                    Full Name <Req />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={selectedPatient.name}
                    onChange={(e) =>
                      setSelectedPatient({
                        ...selectedPatient,
                        name: e.target.value,
                      })
                    }
                    sx={drawerFieldStyle}
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <Typography sx={drawerLabelStyle}>
                    Date of Birth / Age <Req />
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label=""
                        sx={{ flex: 2 }}
                        onChange={handleDateChange}
                        format="DD-MM-YY"
                        maxDate={dayjs()}
                        value={
                          selectedPatient.dob
                            ? dayjs(selectedPatient.dob, "MMMM DD, YYYY")
                            : null
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            sx: drawerFieldStyle,
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "#94a3b8",
                        flexShrink: 0,
                      }}
                    >
                      OR
                    </Typography>
                    <TextField
                      label=""
                      placeholder="Age"
                      size="small"
                      value={selectedPatient.age}
                      onChange={handleAgeChange}
                      sx={{ ...drawerFieldStyle, width: "80px", flexShrink: 0 }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography sx={drawerLabelStyle}>
                    Gender <Req />
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedPatient.gender || ""}
                      onChange={(e) =>
                        setSelectedPatient({
                          ...selectedPatient,
                          gender: e.target.value,
                        })
                      }
                      displayEmpty
                      sx={{
                        borderRadius: "10px",
                        background: "#ffffff",
                        fontSize: "0.875rem",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e5e9ef",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2a54a6",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#b93acf",
                          borderWidth: "1.5px",
                        },
                      }}
                    >
                      <MenuItem
                        value=""
                        disabled
                        sx={{ fontSize: "0.875rem", color: "#94a3b8" }}
                      >
                        Select gender
                      </MenuItem>
                      <MenuItem value="Male" sx={{ fontSize: "0.875rem" }}>
                        Male
                      </MenuItem>
                      <MenuItem value="Female" sx={{ fontSize: "0.875rem" }}>
                        Female
                      </MenuItem>
                      <MenuItem value="Others" sx={{ fontSize: "0.875rem" }}>
                        Others
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={drawerLabelStyle}>
                    Mobile{" "}
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                      (optional)
                    </span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={selectedPatient.mobile}
                    onChange={(e) =>
                      setSelectedPatient({
                        ...selectedPatient,
                        mobile: e.target.value,
                      })
                    }
                    sx={drawerFieldStyle}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={drawerLabelStyle}>
                    Address{" "}
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                      (optional)
                    </span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={selectedPatient.address}
                    onChange={(e) =>
                      setSelectedPatient({
                        ...selectedPatient,
                        address: e.target.value,
                      })
                    }
                    sx={drawerFieldStyle}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={drawerLabelStyle}>
                    Aadhar{" "}
                    <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                      (optional)
                    </span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={selectedPatient.aadhar}
                    onChange={(e) =>
                      setSelectedPatient({
                        ...selectedPatient,
                        aadhar: e.target.value,
                      })
                    }
                    sx={drawerFieldStyle}
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Drawer footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              background: "#ffffff",
              borderTop: "1px solid #e5e9ef",
              display: "flex",
              gap: 1.5,
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCloseDrawer}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "0.82rem",
                fontWeight: 600,
                borderColor: "#e5e9ef",
                color: "#64748b",
                "&:hover": { borderColor: "#94a3b8", background: "#f8fafc" },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmitEdit}
              startIcon={<CheckIcon sx={{ fontSize: "0.95rem !important" }} />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "0.82rem",
                fontWeight: 700,
                background: "linear-gradient(135deg, #2a54a6, #b93acf)",
                boxShadow: "0 4px 14px rgba(185,58,207,0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1e3d8a, #a02db8)",
                  boxShadow: "0 6px 20px rgba(185,58,207,0.4)",
                },
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Log
        openLogDialog={openLogDialog}
        handleCloseLog={handleCloseLog}
        logs={patientLogs}
      />
    </>
  );
};

export default PatientList;
